import { AgentTypes, EventTypes } from '../constants/event';
import { UPSELL_METRIC_VERSION } from '../constants';
import {
  InfoTransmissionMessage,
  TextFrameTransmissionMessage,
  messagingActions,
} from './messagingSlice';
import { AppDispatch } from '../app/store';
import { upsell } from '../utils/upsell';
import logger from '../utils/logger';
import { isExperimentAssignedEvent } from '../redux/features/experiments/experiments.utils';

// The latest EXPERIMENT_ASSIGNED metric, as a list of zero or one events.
const latestExperimentAssigned = (receivedBufferedEvents: any[]) => {
  const latest = [...receivedBufferedEvents]
    .reverse()
    .find(isExperimentAssignedEvent);
  return latest ? [latest] : [];
};

const filterBufferedEvents = (receivedBufferedEvents: any[]) => {
  /* Target events:
        1. ORDERBOARD: transcript
        2. LLM: hypothesis - cart items, order - to get current order sequence id
        3. LLM: language_switch - to set correct language state
        4. LLM: transcript_highlight - all events, processed after text events
        5. asr_promoted_text - all events; with secondary_asr_enabled on these
           ARE the customer transcript, so they are kept whatever agent_type the
           producer stamps them with (HITL-1497)
    */
  const transcriptMessages: any[] = [];
  const entityAnnotationMessages: any[] = [];
  const latestBufferedEvents = {
    hypothesis: false,
    order: false,
    languageSwitch: false,
  };

  for (var i = receivedBufferedEvents.length - 1; i >= 0; i--) {
    const { event, agent_type } = receivedBufferedEvents[i] || {};

    // Checked on `event`, not `agent_type`: the wire agent_type for promoted
    // events is not something HITL should depend on, and they must reach the
    // reducer either way. Grouped with the transcript so they are processed
    // before the trailing hypothesis, which they retire.
    if (event === EventTypes.asrPromotedText) {
      transcriptMessages.push(receivedBufferedEvents[i]);
    } else if (agent_type === AgentTypes.orderBoard) {
      transcriptMessages.push(receivedBufferedEvents[i]);
    } else if (event === EventTypes.transcriptHighlight) {
      entityAnnotationMessages.push(receivedBufferedEvents[i]);
    } else if (Object.values(latestBufferedEvents).includes(false)) {
      switch (event) {
        case EventTypes.hypothesis: {
          if (!latestBufferedEvents.hypothesis) {
            latestBufferedEvents.hypothesis = receivedBufferedEvents[i];
          }
          break;
        }
        case EventTypes.order: {
          if (!latestBufferedEvents.order) {
            latestBufferedEvents.order = receivedBufferedEvents[i];
          }
          break;
        }
        case EventTypes.languageSwitch: {
          if (!latestBufferedEvents.languageSwitch) {
            latestBufferedEvents.languageSwitch = receivedBufferedEvents[i];
          }
          break;
        }
      }
    }
  }

  //Populate transcript messages first, then transcript_highlights (so text events are processed before annotations),
  //followed by 'hypothesis', 'order', 'language_switch'
  return [
    ...transcriptMessages.reverse(),
    ...entityAnnotationMessages.reverse(),
    ...(latestBufferedEvents.hypothesis
      ? [latestBufferedEvents.hypothesis]
      : []),
    ...(latestBufferedEvents.order ? [latestBufferedEvents.order] : []),
    ...(latestBufferedEvents.languageSwitch
      ? [latestBufferedEvents.languageSwitch]
      : []),
    ...latestExperimentAssigned(receivedBufferedEvents),
  ];
};

// Supervised replay: the orderboard events, plus the car's experiment assignment.
const filterSupervisedBufferedEvents = (receivedBufferedEvents: any[]) => [
  ...receivedBufferedEvents.filter(
    (bufferedEvent) => bufferedEvent?.agent_type === AgentTypes.orderBoard
  ),
  ...latestExperimentAssigned(receivedBufferedEvents),
];

// Tracks which HITL source_event ids have already generated an
// upsell_attempted METRIC info event (to avoid duplicates when
// multiple ORDERBOARD text events reference the same source_event).
const upsellSourceEventsProcessed: Record<string, boolean> = {};

const emitUpsellMetric = (
  attempt: NonNullable<ReturnType<typeof upsell.getUpsellAttempted>>,
  dedupeKey: string,
  dispatch: AppDispatch
) => {
  const payload: Partial<InfoTransmissionMessage> = {
    data: {
      type: 'METRIC',
      message: attempt.upsell_phrase,
      metadata: {
        intent: attempt.upsell_intent,
        upsell_attempted: attempt,
        upsell_metric_version: UPSELL_METRIC_VERSION,
      },
    },
  };
  dispatch(messagingActions.sendInfo(payload as any));
  upsellSourceEventsProcessed[dedupeKey] = true;
};

export const handleUpsellAttemptMetricOnTTSAcknowledged = (
  textEvent: TextFrameTransmissionMessage,
  dispatch: AppDispatch
) => {
  // Only interested in ORDERBOARD text events which may include
  // blocked_status.source_event pointing back to the HITL text event.
  if (textEvent.agent_type !== AgentTypes.orderBoard) {
    return;
  }

  const blockedStatus = textEvent.data?.blocked_status as
    | {
        blocked: boolean;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        source_event?: any;
      }
    | undefined;

  // Fallback: when the blocked_status feature is disabled, the ORDERBOARD
  // text event won't carry blocked_status/source_event. In that case, use
  // the current in-memory upsell attempt and match against the TTS payload
  // so we can still emit the upsell_attempted METRIC.
  if (!blockedStatus) {
    const currentUpsellAttempt = upsell.getUpsellAttempted();
    if (
      !currentUpsellAttempt ||
      textEvent.data?.payload !== currentUpsellAttempt.upsell_phrase ||
      upsellSourceEventsProcessed[currentUpsellAttempt.upsell_id]
    ) {
      return;
    }

    emitUpsellMetric(
      currentUpsellAttempt,
      currentUpsellAttempt.upsell_id,
      dispatch
    );
    return;
  }

  const sourceEvent = blockedStatus.source_event as {
    id?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: { payload?: string; metadata?: any };
  };
  const sourceId = sourceEvent?.id;
  if (!sourceId || upsellSourceEventsProcessed[sourceId]) {
    return;
  }

  const upsellIdFromSource = sourceEvent.data?.metadata?.upsell_id;
  const currentUpsellAttempt = upsell.getUpsellAttempted();

  // If this ORDERBOARD text was blocked and the upsell_id matches the
  // current attempt, clear the attempt so that no metric is emitted later.
  if (
    blockedStatus.blocked &&
    currentUpsellAttempt?.upsell_id === upsellIdFromSource
  ) {
    upsell.setUpsellAttempted(null);
    return;
  }

  if (
    !upsellIdFromSource ||
    !currentUpsellAttempt ||
    currentUpsellAttempt.upsell_id !== upsellIdFromSource
  ) {
    return;
  }

  emitUpsellMetric(currentUpsellAttempt, sourceId, dispatch);
};

/**
 * HITL-1701: the AI records its own upsell attempt (an LLM METRIC info carrying
 * `upsell_attempted`) when it hears the agent deliver one of its upsells — the
 * copilot accept, or the agent speaking a card's text verbatim. It then scores
 * the customer's answer itself. When that attempt is for the constraint our
 * open attempt was made for, hand the answer to the AI so the one offer is not
 * answered twice.
 */
export const handleAIUpsellAttemptMetric = (message: {
  event?: string;
  agent_type?: string;
  data?: { metadata?: { upsell_attempted?: { upsell_intent?: string } } };
}) => {
  if (
    message.event !== EventTypes.info ||
    message.agent_type !== AgentTypes.llm
  ) {
    return;
  }
  const aiIntent = message.data?.metadata?.upsell_attempted?.upsell_intent;
  if (!aiIntent) return;
  const ownAttempt = upsell.getUpsellAttempted();
  if (upsell.markAttemptedByAI(aiIntent)) {
    logger.info({
      message: `Upsell answer left to the AI: it recorded its own attempt for ${aiIntent} (HITL upsell_id ${ownAttempt?.upsell_id})`,
    });
  }
};

export { filterBufferedEvents, filterSupervisedBufferedEvents };
